import React, { forwardRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Cpu, Package } from "lucide-react";
import WearableStatusBadge from "./WearableStatusBadge";
import {
  formatMac,
  getStageMeta,
  getUnitTypeLabel,
  wearableIdentity,
} from "./constants";

const WearableRow = forwardRef(({ wearable, isLast }, ref) => {
  // Subscribe to language changes; labels come from constants helpers.
  useTranslation();
  const navigate = useNavigate();
  const identity = wearableIdentity(wearable);

  return (
    <div
      ref={ref}
      onClick={() =>
        navigate(`/v2/admin/manufacturing/wearables/${wearable.ble_mac}`)
      }
      className={`flex cursor-pointer items-center justify-between px-4 py-3 transition duration-150 hover:bg-[#FAFAFA] ${
        !isLast ? "border-b border-[#F3F4F6]" : ""
      }`}
    >
      <div className="flex items-center gap-3">
        <div
          className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${
            identity.hasLabel ? "bg-[#FEF3E7]" : "bg-[#F3F4F6]"
          }`}
        >
          {identity.hasLabel ? (
            <span className="font-mono text-[13px] font-bold tabular-nums text-[#E07A3A]">
              {identity.primary}
            </span>
          ) : (
            <Cpu size={18} className="text-[#6B7280]" />
          )}
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-[#111827]">
            {identity.primary}
          </p>
          <p className="truncate font-mono text-xs text-[#6B7280]">
            {identity.secondary || formatMac(wearable.ble_mac)}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-6">
        {wearable.batch_code && (
          <div className="flex items-center gap-1.5 text-xs text-[#6B7280]">
            <Package size={12} />
            <span>{wearable.batch_code}</span>
          </div>
        )}
        <span className="hidden text-xs capitalize text-[#9CA3AF] sm:inline">
          {wearable.unit_type
            ? getUnitTypeLabel(wearable.unit_type)
            : wearable.unit_type}
        </span>
        {/* For in-validation units the sub-stage is the useful signal, so show
            it alongside the status pill. */}
        {wearable.status === "in_validation" && wearable.validation_stage && (
          <span className="hidden rounded bg-[#F3F4F6] px-2 py-0.5 text-[11px] font-medium text-[#6B7280] md:inline">
            {getStageMeta(wearable.validation_stage).label}
          </span>
        )}
        <WearableStatusBadge status={wearable.status} />
      </div>
    </div>
  );
});

WearableRow.displayName = "WearableRow";

export default WearableRow;
